import React, { useEffect, useState } from "react";
import { Card, Table, Button, Space, Form, Input, Popconfirm, Tooltip } from "antd";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchNewsletterSubscribers,
  deleteNewsletterSubscriber,
} from "../../../api/newsletterApi";
import { toastError, toastSuccess } from "../../../utils/toast";

export default function NewsletterPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchNewsletterSubscribers();
      setRows(data);
      setFiltered(data);
    } catch {
      toastError("Failed to load newsletter emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (values) => {
    const q = (values.search || "").trim().toLowerCase();
    if (!q) {
      setFiltered(rows);
      return;
    }
    setFiltered(rows.filter((row) => (row.email || "").toLowerCase().includes(q)));
  };

  const onReset = () => {
    searchForm.resetFields();
    setFiltered(rows);
  };

  const handleDelete = async (id) => {
    try {
      await deleteNewsletterSubscriber(id);
      toastSuccess("Email deleted");
      load();
    } catch {
      toastError("Failed to delete email");
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      render: (t) => <strong>{t}</strong>,
    },
    {
      title: "Subscribed At",
      dataIndex: "created_at",
      width: 180,
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Delete this email?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Newsletter Emails</h2>
        <Form
          form={searchForm}
          layout="inline"
          onFinish={onSearch}
          style={{ display: "flex", gap: 8 }}
        >
          <Form.Item name="search">
            <Input
              allowClear
              placeholder="Search by email"
              prefix={<SearchOutlined />}
              style={{ minWidth: 260 }}
              onPressEnter={() => searchForm.submit()}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Search
              </Button>
              <Button onClick={onReset}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />
    </Card>
  );
}
